import * as React from 'react';
import { Button, ButtonGroup, Panel } from 'react-bootstrap';
import ConfirmationModal from '../../../../components/common/ConfirmationModal';
import * as Feature from '../../../../shared/Features';
import { Translate } from '../../../../shared/LanguageService';
import * as PhotoStatus from '../../../../shared/PhotoStatus';
import { AuthenticatedUser, UserRole } from '../../../../shared/types';
import RetrieveOrderPanel from './retrieveOrderAsPhotographer/RetrieveOrderPanel';
import { ICost } from "../../../../shared/localization/currency";

export type IPhotoStatus = {
    canReapprove: boolean;
    currentStatus: number;
    isCorrectNumberOfImages: boolean;
    normalImagesAvailable: boolean;
    photosInRows: any[];
};

interface IProps {
    contentProvider: any;
    contentProviderValue: number;
    photoStatus: IPhotoStatus;
    orderId: number;
    orderApiUrl: string;
    user: any;
    isEditorUploadDoneEnabled: () => boolean;
    doneEditingImages: () => void;
    canPhotographerApprove: () => boolean;
    approveByPhotographer: () => void;
    approveByCustomer: () => void;
    reapproveByCustomer: () => void;
    reDeliver: () => void;
    resetRejection: () => void;
    showResetDeliveryConfirmationModal: () => void;
    orderNeedsConfirmation: boolean;
    extraImages: number;
    onRetrieveOrder: () => Promise<void>;
    extraImageCost: ICost | null;
}

interface IState {
    showConfirmationModal: boolean;
    modalText: string | JSX.Element;
}

function isPhotographer(user: AuthenticatedUser) {
    return user.details.role === UserRole.Photographer;
}

class ImageProcessingStatusAction extends React.Component<IProps, IState> {
    constructor(props: IProps, context: any) {
        super(props, context);

        this.state = {
            showConfirmationModal: false,
            modalText: <div>"Godkänn bilder?"</div>
        };
    }

    vitecBrokerSystemValue() {
        return 3;
    }

    canResetDelivery() {
        return (
            this.props.contentProvider &&
            (this.props.photoStatus.currentStatus === PhotoStatus.DeliveryStarted ||
                this.props.photoStatus.currentStatus === PhotoStatus.Delivered ||
                this.props.photoStatus.currentStatus === PhotoStatus.DeliveryError)
        );
    }

    getConfirmModalText = (numberOfExtraImages: number, extraImageCost: ICost | null) => {
        let extraImages = numberOfExtraImages.toString();
        let warningText = '';
        let extraImagesSingle = Translate(
            'image-processing/page/status-actions/warning-text/extra-single'
        );
        let extraImagesMultiple = Translate(
            'image-processing/page/status-actions/warning-text/extra-multiple'
        );

        if (numberOfExtraImages > 0 && extraImageCost !== null) {
            warningText = Translate(
                'image-processing/page/status-actions/warning-text-with-price',
                {
                    numberOfExtraImages:
                        numberOfExtraImages > 1
                            ? extraImages + ' ' + extraImagesMultiple
                            : extraImages + ' ' + extraImagesSingle,
                    costOfExtraImages: extraImageCost.toString(numberOfExtraImages * extraImageCost.value)
                }
            );
        } else if (numberOfExtraImages > 0) {
            warningText = Translate('image-processing/page/status-actions/warning-text');
        } else {
            warningText = Translate('image-processing/page/status-actions/warning-text-no-extra');
        }

        let extraImagesText =
            numberOfExtraImages > 0 && extraImageCost === null
                ? warningText
                      .toString()
                      .replace(
                          '#numberOfExtraImages',
                          numberOfExtraImages > 1
                              ? extraImages + ' ' + extraImagesMultiple
                              : extraImages + ' ' + extraImagesSingle
                      )
                : warningText;

        return extraImagesText;
    };

    toggleModal = () => {
        let toggleModal = !this.state.showConfirmationModal;

        this.setState({
            showConfirmationModal: toggleModal,
            modalText: this.getConfirmModalText(this.props.extraImages, this.props.extraImageCost)
        });
    };

    render() {
        return (
            <div>
                {this.props.photoStatus.currentStatus === PhotoStatus.Uploading &&
                (Feature.hasFeature(Feature.ApproveImagesByPhotographer, this.props.user) ||
                    (Feature.hasFeature(Feature.EditCustomerImages, this.props.user) &&
                        this.props.photoStatus.hasEditCustomerImages)) ? (
                    <Panel>
                        <Panel.Heading>
                            <Panel.Title>
                                {
                                    <div>
                                        {Translate(
                                            'image-processing/page/status-actions/header/photographer-approve-unedited-images'
                                        )}
                                    </div>
                                }
                            </Panel.Title>
                        </Panel.Heading>
                        <Panel.Body>
                            {Translate(
                                'image-processing/page/status-actions/description/photographer-approve-unedited-images'
                            )}
                            <ButtonGroup className="pull-right">
                                <Button
                                    bsStyle="primary"
                                    data-testid="approve-unedited-upload"
                                    disabled={!this.props.canPhotographerApprove()}
                                    onClick={this.props.approveByPhotographer}
                                >
                                    {Translate(
                                        'image-processing/page/status-actions/button-label/approve'
                                    )}
                                </Button>
                            </ButtonGroup>
                        </Panel.Body>
                    </Panel>
                ) : null}

                {this.props.photoStatus.currentStatus === PhotoStatus.UploaderApproved &&
                isPhotographer(this.props.user) &&
                Feature.hasFeature(Feature.ApproveImagesByPhotographer, this.props.user) ? (
                    <RetrieveOrderPanel
                        labels={{
                            title: Translate(
                                'image-processing/page/status-actions/header/retrieve-order'
                            ),
                            description: Translate(
                                'image-processing/page/status-actions/description/retrieve-order'
                            ),
                            buttonText: Translate(
                                'image-processing/page/status-actions/button-label/retrieve-order'
                            )
                        }}
                        orderId={this.props.orderId}
                        onRetrieveOrder={this.props.onRetrieveOrder}
                    />
                ) : null}

                {this.props.isEditorUploadDoneEnabled() && (
                    <Panel>
                        <Panel.Heading>
                            <Panel.Title>
                                {
                                    <div>
                                        {Translate(
                                            'image-processing/page/status-actions/header/done-editing-images'
                                        )}
                                    </div>
                                }
                            </Panel.Title>
                        </Panel.Heading>
                        <Panel.Body>
                            {Translate(
                                'image-processing/page/status-actions/description/done-editing-images'
                            )}
                            <ButtonGroup className="pull-right">
                                <Button
                                    bsStyle="primary"
                                    data-testid="done-editing-images"
                                    onClick={this.props.doneEditingImages}
                                >
                                    {Translate(
                                        'image-processing/page/status-actions/button-label/done'
                                    )}
                                </Button>
                            </ButtonGroup>
                        </Panel.Body>
                    </Panel>
                )}

                {this.props.photoStatus.currentStatus === PhotoStatus.Se360Disapproved &&
                    Feature.hasFeature(Feature.ResetRejectedImages, this.props.user) && (
                        <Panel>
                            <Panel.Heading>
                                <Panel.Title>
                                    {
                                        <div>
                                            {Translate(
                                                'image-processing/page/status-actions/header/reset-rejected-images'
                                            )}
                                        </div>
                                    }
                                </Panel.Title>
                            </Panel.Heading>
                            <Panel.Body>
                                {Translate(
                                    'image-processing/page/status-actions/description/reset-rejected-images'
                                )}
                                <ButtonGroup className="pull-right">
                                    <Button bsStyle="primary" onClick={this.props.resetRejection}>
                                        {Translate(
                                            'image-processing/page/status-actions/button-label/reset-rejected-images'
                                        )}
                                    </Button>
                                </ButtonGroup>
                            </Panel.Body>
                        </Panel>
                    )}

                {(this.props.photoStatus.currentStatus === PhotoStatus.Se360Approved ||
                    (this.props.photoStatus.currentStatus >= PhotoStatus.CustomerApproved &&
                        this.props.orderNeedsConfirmation)) &&
                    Feature.hasFeature(Feature.ApproveImagesByCustomer, this.props.user) && (
                        <Panel>
                            <Panel.Heading>
                                <Panel.Title>
                                    {
                                        <div>
                                            {Translate(
                                                'image-processing/page/status-actions/header/customer-approve-edited-images'
                                            )}
                                        </div>
                                    }
                                </Panel.Title>
                            </Panel.Heading>
                            <Panel.Body>
                                {Translate(
                                    'image-processing/page/status-actions/description/customer-approve-edited-images'
                                )}
                                <ButtonGroup className="pull-right">
                                    <Button bsStyle="primary" onClick={this.toggleModal}>
                                        {Translate(
                                            'image-processing/page/status-actions/button-label/approve'
                                        )}
                                        {this.state.showConfirmationModal && (
                                            <ConfirmationModal
                                                extraValidation={false}
                                                extraValidationText={''}
                                                closeModal={this.toggleModal}
                                                title={Translate(
                                                    'image-processing/page/status-actions/header/customer-approve-edited-images'
                                                )}
                                                text={this.state.modalText}
                                                doButtonLabel={Translate(
                                                    'image-processing/page/status-actions/button-label/approve'
                                                )}
                                                cancelButtonLabel={Translate(
                                                    'delete/modal/footer/button-label/cancel'
                                                )}
                                                doButtonStyle="success"
                                                do={this.props.approveByCustomer}
                                                disableSubmit={false}
                                                initialSubmitDisabling={false}
                                                onCheckboxToggle={() => {}}
                                            />
                                        )}
                                    </Button>
                                </ButtonGroup>
                            </Panel.Body>
                        </Panel>
                    )}

                {this.props.photoStatus.canReapprove &&
                    Feature.hasFeature(Feature.ApproveImagesByCustomer, this.props.user) && (
                        <Panel>
                            <Panel.Heading>
                                <Panel.Title>
                                    {
                                        <div>
                                            {Translate(
                                                'image-processing/page/status-actions/header/customer-approve-edited-images'
                                            )}
                                        </div>
                                    }
                                </Panel.Title>
                            </Panel.Heading>
                            <Panel.Body>
                                {Translate(
                                    'image-processing/page/status-actions/description/customer-approve-edited-images'
                                )}
                                <ButtonGroup className="pull-right">
                                    <Button
                                        bsStyle="primary"
                                        onClick={this.props.reapproveByCustomer}
                                    >
                                        {Translate(
                                            'image-processing/page/status-actions/button-label/approve'
                                        )}
                                    </Button>
                                </ButtonGroup>
                            </Panel.Body>
                        </Panel>
                    )}
                {this.props.photoStatus.currentStatus >= PhotoStatus.CustomerApproved &&
                    this.props.contentProviderValue === this.vitecBrokerSystemValue() && (
                        <Panel>
                            <Panel.Heading>
                                <Panel.Title>
                                    {
                                        <div>
                                            {Translate(
                                                'image-processing/page/status-actions/header/vitec-broker-system/title'
                                            )}
                                        </div>
                                    }
                                </Panel.Title>
                            </Panel.Heading>
                            <Panel.Body>
                                <a
                                    href={
                                        this.props.orderApiUrl +
                                        '/deliver/vitecmaklarsystem?orderId=' +
                                        this.props.orderId
                                    }
                                    target="_blank"
                                >
                                    {Translate(
                                        'image-processing/page/status-actions/header/vitec-broker-system/delivery-link'
                                    )}
                                </a>
                            </Panel.Body>
                        </Panel>
                    )}

                {this.props.photoStatus.currentStatus === PhotoStatus.DeliveryError &&
                    Feature.hasFeature(Feature.ReDeliverImages, this.props.user) && (
                        <Panel className="panel-danger">
                            <Panel.Heading>
                                <Panel.Title>
                                    {
                                        <div>
                                            {Translate(
                                                'image-processing/page/status-actions/header/re-deliver-images'
                                            )}
                                        </div>
                                    }
                                </Panel.Title>
                            </Panel.Heading>
                            <Panel.Body>
                                {Translate(
                                    'image-processing/page/status-actions/description/re-deliver-images'
                                )}
                                <ButtonGroup className="pull-right">
                                    <Button bsStyle="danger" onClick={this.props.reDeliver}>
                                        {Translate(
                                            'image-processing/page/status-actions/button-label/re-deliver'
                                        )}
                                    </Button>
                                </ButtonGroup>
                            </Panel.Body>
                        </Panel>
                    )}

                {this.canResetDelivery() &&
                    Feature.hasFeature(Feature.ResetContentProviderDelivery, this.props.user) && (
                        <Panel bsStyle="default">
                            <Panel.Heading>
                                <Panel.Title>
                                    {
                                        <div>
                                            {Translate(
                                                'image-processing/page/status-actions/header/reset-delivery'
                                            )}
                                        </div>
                                    }
                                </Panel.Title>
                            </Panel.Heading>
                            <Panel.Body>
                                {Translate(
                                    'image-processing/page/status-actions/description/reset-delivery'
                                )}
                                <ButtonGroup className="pull-right">
                                    <Button
                                        bsStyle="danger"
                                        onClick={this.props.showResetDeliveryConfirmationModal}
                                    >
                                        {Translate(
                                            'image-processing/page/status-actions/button-label/reset-delivery'
                                        )}
                                    </Button>
                                </ButtonGroup>
                            </Panel.Body>
                        </Panel>
                    )}
            </div>
        );
    }
}

export default ImageProcessingStatusAction;
